import {useTranslation} from 'react-i18next'
import type {TFunction} from 'i18next'
import {Button} from '@/components/ui/button'
import {HelpIcon} from '@/components/help-icon'
import {Tag} from '@/components/ui/tag'
import {CeldaActo, CeldaEstado} from '@/components/ui/celdas-tabla'
import type {Column} from '@/components/data-table'
import {useIsMobile} from '@/hooks/common/use-media-query'
import type {FilaCumplimiento} from '@/hooks/empresa/cumplimiento/use-cumplimiento'
import {formatDateOnly, formatShortDate} from '@/lib/risk'

const TONOS = {
  verde: 'green',
  amarillo: 'amber',
  rojo: 'red',
  en_curso: 'enCurso',
  gris: 'neutral',
} as const

type Color = keyof typeof TONOS

/** Actos de firmante; los demás son captura ordinaria (`require_operador`). */
const FIRMANTE = new Set(['confirmar', 'confirmarFecha', 'volverAExigir', 'fijarCifra'])

/** Acto de custodia de salud (§4): `_assert_custodio` devuelve 403 al dueño aunque sea firmante. */
const CUSTODIO = new Set(['resolverChoque'])

/** Una fila sin color no lleva chip: el gris ya significa «no le corresponde». `w-fit` evita que
 * el `Tag` se estire al ancho de la columna. */
function Estado({fila}: {fila: FilaCumplimiento}) {
  const {t} = useTranslation()
  if (!fila.color) {
    return (
      <span
        className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground"
        data-testid={`cumplimiento-sin-color-${fila.nombre}`}>
        {t('cumplimiento.noVerificable.titulo')}
      </span>
    )
  }
  return (
    <Tag tone={TONOS[fila.color as Color]} className="w-fit" data-testid={`cumplimiento-color-${fila.nombre}`}>
      {t(`cumplimiento.color.${fila.color}`)}
    </Tag>
  )
}

/** Va en el `title` y un `sr-only` de la celda, no en un `PorQue` por fila (U4). */
function porqueDeLaFila(fila: FilaCumplimiento, t: TFunction): string {
  return [
    ...fila.porque.map((renglon) =>
      t(`requisitoCargo.porque.${renglon.clave}`, {valor: renglon.valor, articulo: fila.norma_articulo ?? ''})
    ),
    t(`cumplimiento.fila.base_${fila.reloj_base}`, {meses: fila.reloj_meses ?? ''}),
    fila.evidencia_el ? t('cumplimiento.fila.evidencia', {fecha: formatDateOnly(fila.evidencia_el)}) : null,
    fila.cuenta && !fila.suma_sugerido ? t('cumplimiento.fila.noSuma') : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

/** Un solo botón por fila (R2), derivado del estado. */
export function useColumnasCumplimiento({
  puedeOperar,
  puedeFirmar,
  puedeCustodiar,
  onAccion,
  onDescartar,
}: {
  puedeOperar: boolean
  puedeFirmar: boolean
  puedeCustodiar: boolean
  onAccion: (fila: FilaCumplimiento) => void
  onDescartar: (fila: FilaCumplimiento) => void
}): Column<FilaCumplimiento>[] {
  const {t} = useTranslation()
  // Se monta o no, nunca se esconde con CSS: bajo `table-fixed` el `<colgroup>` le sigue
  // reservando el ancho. En teléfono el origen viaja dentro de la celda del requisito.
  const enTelefono = useIsMobile()
  return [
    {
      key: 'requisito',
      header: t('cumplimiento.columna.requisito'),
      // Único disparador de ayuda de la lista (U4).
      headerAside: (
        <HelpIcon label={t('cumplimiento.columna.requisitoAyudaLabel')} testId="cumplimiento-ayuda-requisito">
          {t('cumplimiento.columna.requisitoAyuda')}
        </HelpIcon>
      ),
      className: 'pr-3',
      cell: (fila) => {
        const porque = porqueDeLaFila(fila, t)
        const origen = fila.estandar_minimo
          ? t('cumplimiento.origen.estandarMinimo')
          : t('cumplimiento.origen.perfilCargo')
        const clave =
          fila.estado === null ? `cumplimiento.noVerificable.${fila.motivo}` : `cumplimiento.estado.${fila.estado}`
        return (
          <CeldaEstado
            chip={<Estado fila={fila} />}
            nombre={fila.nombre}
            porque={porque}
            porqueTestId={`cumplimiento-porque-${fila.nombre}`}
            /* Una línea de meta: el estado en palabras (D9) y las dos fechas; en teléfono, también el origen. */
            meta={
              <>
                {enTelefono && <span>{origen} · </span>}
                <span data-testid={`cumplimiento-frase-${fila.nombre}`}>
                  {t(clave, {
                    nombre: fila.nombre,
                    fecha: fila.vence_el ? formatDateOnly(fila.vence_el) : '',
                    plazo: fila.plazo ? formatDateOnly(fila.plazo) : '',
                    evidencia: fila.evidencia_el ? formatDateOnly(fila.evidencia_el) : '',
                    dias: fila.dias_para_vencer ?? 0,
                    motivo: fila.decision_motivo ?? '',
                    decisor: fila.decision_por ?? '',
                    decidido: fila.decision_el ? formatDateOnly(fila.decision_el) : '',
                  })}
                </span>
                {' · '}
                {t('cumplimiento.fila.desde', {fecha: formatShortDate(fila.exigible_desde)})}
                {fila.reloj_meses ? ` · ${t('cumplimiento.fila.meses', {meses: fila.reloj_meses})}` : ''}
              </>
            }
          />
        )
      },
    },
    ...(enTelefono
      ? []
      : [
          {
            key: 'origen',
            header: t('cumplimiento.columna.origen'),
            width: 'w-32',
            className: 'pr-2',
            cell: (fila: FilaCumplimiento) => (
              <span className="text-xs text-muted-foreground" data-testid={`cumplimiento-origen-${fila.nombre}`}>
                {fila.estandar_minimo ? t('cumplimiento.origen.estandarMinimo') : t('cumplimiento.origen.perfilCargo')}
              </span>
            ),
          },
        ]),
    {
      key: 'accion',
      header: t('cumplimiento.columna.accion'),
      width: 'w-32',
      className: 'text-right',
      stopClick: true,
      cell: (fila) => {
        const canEdit = CUSTODIO.has(fila.boton ?? '')
          ? puedeCustodiar
          : FIRMANTE.has(fila.boton ?? '')
            ? puedeFirmar
            : puedeOperar
        return (
          <CeldaActo
            acto={
              fila.boton && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!canEdit}
                  onClick={() => onAccion(fila)}
                  data-testid={`cumplimiento-boton-${fila.nombre}`}>
                  {t(`cumplimiento.boton.${fila.boton}`)}
                </Button>
              )
            }
            secundario={
              puedeFirmar &&
              fila.estado !== null &&
              fila.estado !== 'no_le_corresponde' && (
                <button
                  type="button"
                  className="text-xs underline text-muted-foreground"
                  onClick={() => onDescartar(fila)}
                  data-testid={`cumplimiento-descartar-${fila.nombre}`}>
                  {t('cumplimiento.boton.noLeCorresponde')}
                </button>
              )
            }
          />
        )
      },
    },
  ]
}
